'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import { IconAiSparkles, IconSearch, IconHome } from '@repo/svgs';

export interface AiMiniSearchBarProps {
  placeholder?: string;
  /**
   * `desktop` (Figma frame 42): [home button] · text · [search button]. `mobile`:
   * a simple sparkle + text pill (no buttons) that opens the full-page view.
   */
  variant?: 'desktop' | 'mobile';
  /** Desktop left home button → open the expanded header. */
  onHomeClick?: () => void;
  /** Text tap → open the expanded search (active). On mobile, the whole pill. */
  onTextClick?: () => void;
  /** Desktop right search button → open listing results (verified default). */
  onSearchClick?: () => void;
  /** Mobile: whole-pill tap (opens the full-page view). */
  onClick?: () => void;
  className?: string;
}

/**
 * Compact AI search shown in the header once the hero search scrolls out of view
 * (desktop: portaled into `#site-header-affix`; mobile: the condensed top bar).
 */
export function AiMiniSearchBar({
  placeholder = 'What are you looking for?',
  variant = 'desktop',
  onHomeClick,
  onTextClick,
  onSearchClick,
  onClick,
  className,
}: AiMiniSearchBarProps) {
  if (variant === 'mobile') {
    // Simple sparkle + text pill; the whole thing opens the full-page view.
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label="Search apartments with AI"
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 rounded-full border border-secondary bg-primary py-3 px-4 text-left shadow',
          className,
        )}
      >
        <IconAiSparkles className="w-4 h-4 icon-tertiary shrink-0" />
        <span className="min-w-0 flex-1 truncate text-body-2 text-tertiary">{placeholder}</span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        // Auto-width (hugs its content) to match the design — the placeholder is
        // never truncated in the header affix slot.
        'inline-flex items-center gap-2 rounded-full border border-secondary bg-primary p-2 shadow',
        'min-h-[52px]',
        className,
      )}
    >
      {/* Left: home icon button (accent icon on light bg) → expand the header. */}
      <button
        type="button"
        onClick={onHomeClick}
        aria-label="Open search"
        className="flex h-9 w-9 p-2 shrink-0 items-center justify-center rounded-full bg-active"
      >
        <IconHome className="h-4 w-4 icon-accent" />
      </button>
      {/* Middle: text → open the expanded search (active). `text-body-2` belongs on the
          button, not the span: from the span the button's inherited 24px strut still sets
          the line box, making this 40px and the bar 4px taller than the other mini bars. */}
      <button
        type="button"
        onClick={onTextClick}
        aria-label="Search apartments with AI"
        className="p-2 text-left text-body-2 text-secondary"
      >
        <span className="whitespace-nowrap">{placeholder}</span>
      </button>
      {/* Right: search button → listing results with verified default. */}
      <button
        type="button"
        onClick={onSearchClick}
        aria-label="Search"
        className="flex h-9 w-9 p-2 shrink-0 items-center justify-center rounded-full bg-accent"
      >
        <IconSearch className="h-4 w-4 icon-inverted" />
      </button>
    </div>
  );
}

export default AiMiniSearchBar;
